import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { fromJS } from 'immutable';
import * as _ from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import injectReducer from 'utils/injectReducer';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import DatePicker from '../components/DatePicker/index';
import Textbox from '../components/Textbox';
import TextArea from '../components/TextArea';
import FlexBox from '../../../components/FlexBox';
import OptionsContainer from '../components/OptionsContainer';
import { makeSelectRentalCarProtectionTravellerTugo, makeSelectRentalCarProtectionTravellerTugoErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import WizardNavigationButtons from '../components/WizardNavigationButtons';
import { handleFormDataChange, nextSlide, resetFormFields } from './actions';
import { ErrorMessage, Validate, ValidateGroup } from '../../../components/Validation';
import { saveFormData } from '../actions';
import { AdditionalList } from '../Tugo.ADAndDBenefits/components/Additional';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { required, checkDateOnly } from '../../../utils/validation/validator';
import { makeSelectInsuredsList, makeSelectIsClaimForFieldEnabled, makeSelectFormHasChanges } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';

export class RentalCarProtectionTravellerTugo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = _.uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onChange = (propertyName) => (event) => {
    const propertyFullName = `form.${propertyName}`;
    if (event === null) {
      this.props.onFormDataChange(propertyFullName, null);
      this.props.setFormHasChanges(true);
      return;
    }
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyFullName, value);
    this.props.setFormHasChanges(true);
  };

  handleExpenseTypeChange = (formField) => (data) => {
    this.props.onFormDataChange(`form.${formField}`, fromJS(data));
    this.props.setFormHasChanges(true);
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onResize() {
    const { adjust } = this.props;
    if (adjust) adjust();
  }

  appendIds = (array) =>
    (array || []).map((item) => {
      const newItem = { ...item };
      if (!newItem.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState((s) => ({ validationCounter: s.validationCounter + 1 }));
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      this.setState((s) => ({ validationCounter: s.validationCounter + 1 }));
    } else {
      this.props.makeValid(this.props.step);
    }

    this.props.onFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') return;
    if (e.key === 'Tab') e.preventDefault();
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.props.onFormDataSave();
    if (this.nextButton) this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  handleDayChange = (propertyName) => (day) => {
    const dayWithoutTimeZone = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(`form.${propertyName}`, dayWithoutTimeZone);
    this.props.setFormHasChanges(true);
  };

  renderExpensesItems(formField) {
    const items = this.props.form[formField];
    const itemsArray = Array.isArray(items) ? items : [];
    const newItems = this.appendIds(itemsArray);
    const { isSummary, submitted, insuredsList, isClaimForFieldEnabled, intl } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="mt-2">
        <AdditionalList
          data={newItems}
          onChange={this.handleExpenseTypeChange(formField)}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
          insuredsList={insuredsList}
          isClaimForFieldEnabled={isClaimForFieldEnabled}
          intl={intl}
        />
      </div>
    );
  }

  render() {
    const { isSummary, submitted, loading, intl } = this.props;
    const isReadOnly = submitted || isSummary;
    const {
      carRentalStartDate,
      carRentalEndDate,
      pickUpLocation,
      dropOffLocation,
      nameOfDriver,
      authorityToWhomReported,
      describeAccident,
      causedByAnotherParty,
      responsiblePartyInfo,
    } = this.props.form;

    return (
      <div className="app-content">
        <div className="row">
          <div className="col-sm-12 form-group pt-3">
            <p>
              <FormattedMessage {...messages.sectionHeader} />
            </p>

            <div className="row">
              <div className="col-sm-6 form-group">
                <Validate
                  name="carRentalStartDate"
                  validators={[checkDateOnly, required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={carRentalStartDate}
                  fieldName={messages.carRentalStartDate}
                >
                  <DatePicker
                    disabled={isReadOnly}
                    type="text"
                    onDayChange={this.handleDayChange('carRentalStartDate')}
                    value={carRentalStartDate}
                    placeholder="dd/mm/yyyy"
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.carRentalStartDate) }} />
                  </ErrorMessage>
                  <ErrorMessage for="checkDateOnly">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.carRentalStartDate) }} />
                  </ErrorMessage>
                </Validate>
              </div>
              <div className="col-sm-6 form-group">
                <Validate
                  name="carRentalEndDate"
                  validators={[checkDateOnly, required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={carRentalEndDate}
                  fieldName={messages.carRentalEndDate}
                >
                  <DatePicker
                    disabled={isReadOnly}
                    type="text"
                    onDayChange={this.handleDayChange('carRentalEndDate')}
                    value={carRentalEndDate}
                    placeholder="dd/mm/yyyy"
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.carRentalEndDate) }} />
                  </ErrorMessage>
                  <ErrorMessage for="checkDateOnly">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.carRentalEndDate) }} />
                  </ErrorMessage>
                </Validate>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 form-group">
                <Validate
                  name="pickUpLocation"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={pickUpLocation}
                  fieldName={messages.pickUpLocation}
                >
                  <Textbox
                    name="pickUpLocation"
                    type="text"
                    onChange={this.onChange('pickUpLocation')}
                    value={pickUpLocation || ''}
                    disabled={isReadOnly}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.pickUpLocation) }} />
                  </ErrorMessage>
                </Validate>
              </div>
              <div className="col-sm-6 form-group">
                <Validate
                  name="dropOffLocation"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={dropOffLocation}
                  fieldName={messages.dropOffLocation}
                >
                  <Textbox
                    name="dropOffLocation"
                    type="text"
                    onChange={this.onChange('dropOffLocation')}
                    value={dropOffLocation || ''}
                    disabled={isReadOnly}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.dropOffLocation) }} />
                  </ErrorMessage>
                </Validate>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 form-group">
                <Validate
                  name="nameOfDriver"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={nameOfDriver}
                  fieldName={messages.nameOfDriver}
                >
                  <Textbox
                    name="nameOfDriver"
                    type="text"
                    onChange={this.onChange('nameOfDriver')}
                    value={nameOfDriver || ''}
                    disabled={isReadOnly}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.nameOfDriver) }} />
                  </ErrorMessage>
                </Validate>
              </div>
              <div className="col-sm-6 form-group">
                <Validate
                  name="authorityToWhomReported"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={authorityToWhomReported}
                  fieldName={messages.authorityToWhomReported}
                >
                  <Textbox
                    name="authorityToWhomReported"
                    type="text"
                    onChange={this.onChange('authorityToWhomReported')}
                    value={authorityToWhomReported || ''}
                    disabled={isReadOnly}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={{ fieldName: intl.formatMessage(messages.authorityToWhomReported) }} />
                  </ErrorMessage>
                </Validate>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-12 form-group">
                <label><FormattedMessage {...messages.describeAccident} /></label>
                <TextArea
                  name="describeAccident"
                  type="text"
                  onChange={this.onChange('describeAccident')}
                  value={describeAccident || ''}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-sm-12 form-group">
                <label><FormattedMessage {...messages.causedByAnotherParty} /></label>
                <FlexBox>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="radio"
                      value="true"
                      checked={causedByAnotherParty === 'true'}
                      onChange={this.onChange('causedByAnotherParty')}
                    />
                    <FormattedMessage {...messages.yes} />
                  </OptionsContainer>
                  <OptionsContainer>
                    <input
                      disabled={isReadOnly}
                      type="radio"
                      value="false"
                      checked={causedByAnotherParty === 'false'}
                      onChange={this.onChange('causedByAnotherParty')}
                    />
                    <FormattedMessage {...messages.no} />
                  </OptionsContainer>
                </FlexBox>
              </div>
            </div>

            {causedByAnotherParty === 'true' && (
              <div className="row">
                <div className="col-sm-12 form-group">
                  <label><FormattedMessage {...messages.responsiblePartyInfo} /></label>
                  <TextArea
                    name="responsiblePartyInfo"
                    type="text"
                    onChange={this.onChange('responsiblePartyInfo')}
                    value={responsiblePartyInfo || ''}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            )}

            <div className="col-sm-12 form-group ml-2 mt-2 p-3 bg-light rounded">
              {this.renderExpensesItems('rentalCarItems')}
            </div>
          </div>
        </div>

        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleSaveButtonClick}
            canMoveNext
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            showSpinnerForElement={this.showSpinnerForElement}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => { this.nextButton = ref; }}
          />
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

RentalCarProtectionTravellerTugo.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  errors: PropTypes.any,
  loading: PropTypes.bool,
  currentStep: PropTypes.number,
  step: PropTypes.number,
  adjust: PropTypes.func,
  onNext: PropTypes.func,
  makeError: PropTypes.func,
  makeValid: PropTypes.func,
  isReadOnly: PropTypes.bool,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  insuredsList: PropTypes.any,
  isClaimForFieldEnabled: PropTypes.bool,
  intl: intlShape.isRequired,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectRentalCarProtectionTravellerTugo(),
  errors: makeSelectRentalCarProtectionTravellerTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  insuredsList: makeSelectInsuredsList(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) => dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withReducer = injectReducer({
  key: 'rentalCarProtectionTravellerTugo',
  reducer,
});

export default compose(
  withReducer,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
)(RentalCarProtectionTravellerTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.RentalCarProtectionTraveller/index.js